import { getNotify } from '../context/NotificationContext';

/**
 * Maps backend error codes to user-friendly messages.
 */
const ERROR_MESSAGES = {
  VALIDATION_ERROR: 'Please check your input and try again.',
  AUTHENTICATION_ERROR: 'Your session has expired. Please log in again.',
  AUTHORIZATION_ERROR: 'You do not have permission to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  RATE_LIMIT_EXCEEDED: 'Too many requests. Please wait and try again.',
  CONFLICT: 'This resource already exists.',
  FILE_TOO_LARGE: 'The file is too large. Please use a smaller file.',
  INTERNAL_ERROR: 'Something went wrong. Please try again later.',
};

/**
 * Centralized API error handler.
 * Call from axios response interceptor to show consistent notifications.
 *
 * @param {Error} error - Axios error object
 * @returns {{ message: string, code: string, status: number, details: Array|null } | null}
 */
export function handleApiError(error) {
  // Ignore intentionally canceled requests (auth redirects)
  if (error.code === 'ERR_CANCELED') return null;

  const notify = getNotify();
  const response = error.response;

  // Network error — no response at all
  if (!response) {
    notify?.('Unable to connect to the server. Please check your internet connection.', 'error');
    return { message: 'Network error', code: 'NETWORK_ERROR', status: 0, details: null };
  }

  const { data, status } = response;
  const message = data?.message || ERROR_MESSAGES[data?.code] || ERROR_MESSAGES.INTERNAL_ERROR;
  const code = data?.code || 'UNKNOWN';

  // Skip notification for 401s — the interceptor handles redirect
  if (status === 401) {
    return { message, code, status, details: data?.details || null };
  }

  // Show appropriate notification based on status
  if (status === 400 || status === 422) {
    if (data?.details?.length) {
      const fieldErrors = data.details.map((d) => d.message).join('. ');
      notify?.(fieldErrors, 'warning');
    } else {
      notify?.(message, 'warning');
    }
  } else if (status === 403) {
    notify?.(message, 'error');
  } else if (status === 429) {
    notify?.(message, 'warning');
  } else if (status >= 500) {
    notify?.(message, 'error');
  }

  return { message, code, status, details: data?.details || null };
}
